import {useInfiniteQuery, useMutation, useQueryClient, InfiniteData} from '@tanstack/react-query';
import {FundraisersFilter, FundraisersResponse, parseSortParams} from '../model';
import {useCallback, useMemo} from 'react';
import {useNotifications} from '../../../shared';
import {fundraisersApi} from '../api';
import {useSearchParams} from 'react-router';

const DEFAULT_PAGE_SIZE = 25;

export const useFundraisers = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const {createNotification} = useNotifications();

  // Преобразуем URL параметры в объект фильтров для API
  const getFiltersFromParams = useCallback((): FundraisersFilter => {
    const filters: FundraisersFilter = {};

    const query = searchParams.get('query');
    // Значения из URL приводим не приведением типа, а проверкой по списку: неизвестное значение
    // отбрасываем и откатываемся к сортировке по умолчанию вместо 400 от бэкенда. Пару свойство/направление
    // разбираем целиком, чтобы запрос не разъехался с тем, что подсвечено в заголовке таблицы
    const {sortProperty, sortOrder} = parseSortParams(searchParams);
    const categories = searchParams.get('categories');
    const regions = searchParams.get('regions');
    const funds = searchParams.get('funds');
    const statuses = searchParams.get('statuses');

    if (query) filters.query = query;
    if (sortProperty) filters.sortProperty = sortProperty;
    if (sortOrder) filters.sortOrder = sortOrder;
    if (categories) filters.categories = categories.split(',').filter(Boolean);
    if (regions) filters.regions = regions.split(',').filter(Boolean);
    if (funds) filters.funds = funds.split(',').filter(Boolean);
    if (statuses) filters.statuses = statuses.split(',').filter(Boolean);

    return filters;
  }, [searchParams]);

  const infiniteQueryFilters = useMemo(() => {
    const filters = getFiltersFromParams();
    const {after, ...rest} = filters as any;
    return rest;
  }, [getFiltersFromParams]);

  const getInfiniteFundraisers = useInfiniteQuery<
    FundraisersResponse,
    Error,
    InfiniteData<FundraisersResponse>,
    ['fundraisers', 'infinite', Omit<FundraisersFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['fundraisers', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return fundraisersApi.getFundraisers({
        ...infiniteQueryFilters,
        pageSize: DEFAULT_PAGE_SIZE,
        after: pageParam,
      });
    },
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    initialPageParam: undefined,
    staleTime: 1000 * 60,
  });

  const createFundraiserMutation = useMutation({
    mutationFn: fundraisersApi.createFundraiser,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraisers'], refetchType: 'all'});
      createNotification({
        title: 'Сбор успешно создан',
        name: 'createFundraiser',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteFundraisers,
    createFundraiserMutation,
  };
};
